import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Trash2, MapPin, Phone, Mail } from "lucide-react";
import { availabilityApi, apiErrorMessage } from "@/api/client";
import { AvailabilityDialog } from "@/components/forms/AvailabilityDialog";
import { toast } from "sonner";

const TYPE_META = {
  field: { label: "Field", icon: MapPin, cls: "border-teal-200 bg-teal-50 text-teal-700" },
  phone: { label: "Phone", icon: Phone, cls: "border-indigo-200 bg-indigo-50 text-indigo-700" },
  email_desk: { label: "Email/Desk", icon: Mail, cls: "border-amber-200 bg-amber-50 text-amber-700" },
};
const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function iso(d) { return d.toISOString().slice(0, 10); }
function mondayOf(d) { const x = new Date(d); const wd = (x.getDay() + 6) % 7; x.setDate(x.getDate() - wd); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

export default function Availability() {
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(new Date());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // {type, date}

  const monday = mondayOf(anchor);
  const days = [...Array(7)].map((_, i) => addDays(monday, i));
  const from = iso(monday);
  const to = iso(addDays(monday, 6));
  const selectedDay = iso(anchor);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await availabilityApi.list({ from, to });
      setItems(data.items);
    } finally { setLoading(false); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try { await availabilityApi.remove(id); toast.success("Removed"); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const byDate = (dstr) => items.filter((a) => a.business_date === dstr);

  const Block = ({ a }) => {
    const m = TYPE_META[a.availability_type];
    const Icon = m.icon;
    return (
      <div data-testid={`avail-block-${a.id}`} className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs font-semibold ${m.cls}`}>
        <span className="flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" /> {m.label} · {a.full_day ? "All day" : `${a.start_time}–${a.end_time}`}</span>
        <button data-testid={`avail-remove-${a.id}`} onClick={() => remove(a.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-5 pb-40 md:py-8">
      <h1 className="mb-3 font-heading text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">Availability</h1>

      {/* Segmented control */}
      <div className="mb-3 grid grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1" data-testid="availability-view-toggle">
        {[["week", "Week"], ["day", "Day"], ["list", "List"]].map(([v, l]) => (
          <button key={v} data-testid={`avail-view-${v}`} onClick={() => setView(v)}
            className={`rounded-lg py-2 text-sm font-semibold ${view === v ? "bg-white text-teal-700 shadow-sm" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>

      {/* Navigator */}
      <div className="mb-4 flex items-center justify-between">
        <button data-testid="avail-prev" onClick={() => setAnchor(addDays(anchor, view === "day" ? -1 : -7))} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500"><ChevronLeft className="h-4 w-4" /></button>
        <span className="text-sm font-semibold text-slate-700">
          {view === "day" ? anchor.toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "short" })
            : `${monday.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })} – ${addDays(monday, 6).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}`}
        </span>
        <button data-testid="avail-next" onClick={() => setAnchor(addDays(anchor, view === "day" ? 1 : 7))} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500"><ChevronRight className="h-4 w-4" /></button>
      </div>

      {loading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl border border-slate-200 bg-white" />)}</div>
      ) : view === "week" ? (
        <div className="space-y-3" data-testid="availability-week">
          {days.map((d, i) => {
            const dstr = iso(d);
            const blocks = byDate(dstr);
            return (
              <div key={dstr} className="rounded-xl border border-slate-200 bg-white p-3">
                <button onClick={() => { setAnchor(d); setView("day"); }} className="mb-2 flex w-full items-center justify-between text-left">
                  <span className="text-sm font-bold text-slate-800">{DAY_NAMES[i]} {d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</span>
                  <span className="text-xs text-slate-400">{blocks.length || "—"}</span>
                </button>
                <div className="space-y-1.5">{blocks.length ? blocks.map((a) => <Block key={a.id} a={a} />) : <p className="text-xs text-slate-400">Nothing scheduled.</p>}</div>
              </div>
            );
          })}
        </div>
      ) : view === "day" ? (
        <div className="space-y-1.5 rounded-xl border border-slate-200 bg-white p-4" data-testid="availability-day">
          {byDate(selectedDay).length ? byDate(selectedDay).map((a) => <Block key={a.id} a={a} />) : <p className="text-sm text-slate-400">Nothing scheduled for this day. Use the buttons below to add.</p>}
        </div>
      ) : (
        <div className="space-y-2" data-testid="availability-list">
          {items.length ? items.map((a) => (
            <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-3">
              <p className="mb-1 text-xs font-semibold text-slate-500">{new Date(a.start_at).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" })}</p>
              <Block a={a} />
            </div>
          )) : <p className="text-sm text-slate-400">No availability this week.</p>}
        </div>
      )}

      {/* Quick-action bottom bar */}
      <div data-testid="availability-quick-bar" className="fixed inset-x-0 bottom-16 z-30 border-t border-slate-200 bg-white/95 px-3 py-2 backdrop-blur md:bottom-0">
        <div className="mx-auto flex max-w-3xl items-center gap-2">
          <QuickBtn testid="quick-field" icon={MapPin} label="+ Field Day" onClick={() => setDialog({ type: "field", date: view === "day" ? selectedDay : from })} />
          <QuickBtn testid="quick-phone" icon={Phone} label="+ Phone" onClick={() => setDialog({ type: "phone", date: view === "day" ? selectedDay : from })} />
          <QuickBtn testid="quick-email" icon={Mail} label="+ Email/Desk" onClick={() => setDialog({ type: "email_desk", date: view === "day" ? selectedDay : from })} />
        </div>
      </div>

      <AvailabilityDialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}
        availabilityType={dialog?.type} date={dialog?.date} onSaved={load} />
    </div>
  );
}

function QuickBtn({ testid, icon: Icon, label, onClick }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="flex min-h-[48px] flex-1 flex-col items-center justify-center gap-0.5 rounded-lg border border-teal-200 bg-teal-50 px-2 text-[11px] font-semibold text-teal-700 hover:bg-teal-100">
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}
